import React, { useState, useEffect, useCallback } from "react";
import styles from "./learningZonesTab.module.css";
import CreateNewLearningZoneModal from "../learningZoneModals/createNewLearningZoneModal/CreateNewLearningZoneModal";
import LearningToolModal from "../learningToolModal/LearningToolModal";
import { CiCirclePlus } from "react-icons/ci";
import { FaBookOpen } from "react-icons/fa";
import { TiDelete } from "react-icons/ti";
import { ImExit } from "react-icons/im";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

const LearningZonesTab = ({ tabData, isCreator, username, refreshData }) => {
  /* The Learning Zones Tab is used on the Learning Zones Page: there is a Creator tab, which lists learning zones created by the user, and there is a Member tab, which lists learning zones the user is a member of, but did not create. The component displays basic information about each learning zone on the list, links to it, and allows a member to leave the learning zone, and a creator - to deactivate it. The user can also create a new learning zone. */

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [learningZones, setLearningZones] = useState(tabData);
  const navigate = useNavigate();

  /**
   * Get the last update date of a learning zone
   * @param {*} learningZoneId - the id of the learning zone
   * @returns the last update date of the learning zone
   */
  const getLZUpdateDate = async (learningZoneId) => {
    try {
      const res = await axios.get(
        `/learning_zones/last_update/${learningZoneId}`,
        { withCredentials: true }
      );

      if (res.status === 200 && res.data.success) {
        return res.data.lastUpdateDate;
      } else {
        console.error("Error fetching last update date.");
        return "N/A";
      }
    } catch (error) {
      console.error("Error in getLZUpdateDate:", error);
      return "N/A";
    }
  };

  /**
   * Get the last update dates of all learning zones displayed in the tab.
   */
  const updateDates = useCallback(async () => {
    const updatedZones = await Promise.all(
      tabData.map(async (zone) => {
        const updateDate = await getLZUpdateDate(zone.learning_zone_id);
        return {
          ...zone,
          updated_date: updateDate,
        };
      })
    );

    setLearningZones(updatedZones);
  }, [tabData]);

  useEffect(() => {
    setLearningZones(tabData);
    updateDates(); // Fetch and update the last update dates
  }, [tabData, updateDates]);

  /**
   * Show the new learning zone modal
   */
  const handleNewLearningZoneBtnClick = () => {
    setModalOpen(true);
  };

  /**
   * Close the new learning zone modal
   */
  const closeModal = () => {
    setModalOpen(false);
  };

  /**
   * Redirect the user to a newly created learning zone, and show a success toast message.
   * @param {*} learningZoneId - the id of the learning zone to redirect the user to
   */
  const handleAddLearningZone = (learningZoneId) => {
    navigate(`/learningZone/${learningZoneId}`);
    toast.success("New learning zone created successfully!");

    refreshData(); // Refresh data after creating a new learning zone
  };

  /**
   * Show the confirmation modal to allow the user to delete a learning zone
   * @param {*} learningZone - object of the learning zone to be deleted
   */
  const handleDeleteClick = (learningZone) => {
    setSelectedZone(learningZone);
    setDeleteModalOpen(true);
  };

  /**
   * Show a confirmation modal, to allow the user to leave a learning zone.
   * @param {*} learningZone - object of the learning zone the user wants to leave
   */
  const handleLeaveClick = (learningZone) => {
    setSelectedZone(learningZone);
    setLeaveModalOpen(true);
  };

  /**
   * Close the leave learning zone confirmation modal
   */
  const closeLeaveModal = () => {
    setLeaveModalOpen(false);
    setSelectedZone(null);
  };

  /**
   * Close the delete learning zone confirmation modal
   */
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedZone(null);
  };

  /**
   * Delete a learning zone from the database and remove it from the page.
   */
  const confirmDelete = async () => {
    try {
      const res = await axios.put(
        `/learning_zones/deactivate/${selectedZone.learning_zone_id}`
      );
      if (res.status === 200 && res.data.success) {
        toast.success("Learning zone deleted successfully!");
        refreshData(); // Refresh data after deleting a learning zone
      } else {
        toast.error("Error deleting learning zone.");
      }
    } catch (error) {
      toast.error("Error deleting learning zone.");
    }
    closeDeleteModal();
  };

  /**
   * Remove a user from a learning zone
   */
  const confirmLeave = async () => {
    try {
      const res = await axios.delete(
        `/learning_zones/list/remove/self/${selectedZone.learning_zone_id}/${username}`
      );
      if (res.status === 200 && res.data.success) {
        toast.success("You have left the learning zone successfully!");
        refreshData(); // Refresh data after leaving a learning zone
      } else {
        toast.error("Error leaving learning zone.");
      }
    } catch (error) {
      toast.error("Error leaving learning zone.");
    }
    closeLeaveModal();
  };

  const learningZonesList = learningZones.map((learningZone, idx) => (
    <div key={idx} className={styles.singleLearningZone}>
      <Link to={`/learningZone/${learningZone.learning_zone_id}`}>
        <div className={styles.learningZoneHeader}>
          <h3>{learningZone.learning_zone_name}</h3>
          <p className={styles.languageDesc}>
            <span className={styles.learningIcon}>
              <FaBookOpen />
            </span>
            Learning <span>{learningZone.language}</span>
          </p>
        </div>
        <div className={styles.moreInfo}>
          <figure>
            <figcaption>
              By <span>{learningZone.creator_username}</span>
            </figcaption>
            <img
              src={learningZone.img_link || learningZone.creator_img_link}
              alt="Creator"
            />
          </figure>
          <p>
            Members: <span>{learningZone.members_num}</span>
          </p>
          <p className={styles.lastUpdate}>
            Last Update: <span>{learningZone.updated_date}</span>
          </p>
        </div>
      </Link>
      {isCreator && (
        <button
          className={styles.deleteButton}
          onClick={() => handleDeleteClick(learningZone)}
        >
          <TiDelete />
        </button>
      )}

      {!isCreator && (
        <button
          className={styles.leaveButton}
          onClick={() => handleLeaveClick(learningZone)}
        >
          <ImExit />
        </button>
      )}
    </div>
  ));

  return (
    <div className={styles.LearningZonesTab}>
      <div className={styles.btnContainer}>
        {isCreator && (
          <button onClick={handleNewLearningZoneBtnClick}>
            Create a New Learning Zone{" "}
            <span className={styles.btnIcon}>
              <CiCirclePlus />
            </span>
          </button>
        )}
      </div>
      <div className={styles.learningZones}>{learningZonesList}</div>
      {modalOpen && (
        <CreateNewLearningZoneModal
          isOpen={modalOpen}
          onClose={closeModal}
          onCreate={handleAddLearningZone}
        />
      )}
      {deleteModalOpen && (
        <LearningToolModal
          show={deleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          message={`Are you sure you want to delete the learning zone "${selectedZone?.learning_zone_name}"?`}
          approveBtnText="Delete"
          cancelBtnText="Cancel"
        />
      )}

      {leaveModalOpen && (
        <LearningToolModal
          show={leaveModalOpen}
          onClose={closeLeaveModal}
          onConfirm={confirmLeave}
          message={`Are you sure you want to leave the learning zone "${selectedZone?.learning_zone_name}"?`}
          approveBtnText="Leave"
          cancelBtnText="Cancel"
        />
      )}
    </div>
  );
};

export default LearningZonesTab;
